import { createContext, useContext, useState, ReactNode } from "react";
import { SettingsModal } from "@/components/settings/SettingsModal";

export const SETTINGS_SECTIONS = [
  "appearance",
  "slides",
  "document-types",
  "ai",
  "account",
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** Older deep-link ids → current section ids. */
const LEGACY_SECTIONS: Record<string, SettingsSection> = {
  content: "document-types",
};

function resolveSection(tab?: string): SettingsSection {
  if (tab && (SETTINGS_SECTIONS as readonly string[]).includes(tab)) {
    return tab as SettingsSection;
  }
  if (tab && tab in LEGACY_SECTIONS) return LEGACY_SECTIONS[tab];
  return "appearance";
}

interface SettingsContextType {
  openSettings: (tab?: SettingsSection | string) => void;
  closeSettings: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [defaultSection, setDefaultSection] = useState<SettingsSection>("appearance");

  const openSettings = (tab?: SettingsSection | string) => {
    setDefaultSection(resolveSection(tab));
    setIsOpen(true);
  };

  const closeSettings = () => {
    setIsOpen(false);
  };

  return (
    <SettingsContext.Provider value={{ openSettings, closeSettings }}>
      {children}
      <SettingsModal
        isOpen={isOpen}
        onClose={closeSettings}
        defaultSection={defaultSection}
      />
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
